<template>    
    <page-layout>
        <is-dev>
            <infinity-page
                :loading="loading"
                icon="$vuetify.icons.universe"
                color="teal"
            >
                <developer-drawer />
                <template #title>
                    <h1
                        style="font-weight: 300; font-family: lato; font-weight: bold; letter-spacing: initial;"
                        v-html="'Development settings'"
                    />                     
                    <h2 
                        style="font-weight: 200"
                        v-html="'Set up your development environment'"
                    />                     
                </template>
                <template #actions>                    
                    <v-spacer />                    
                </template>                
                <v-sheet>                    
                    <v-container>
                        <v-card
                            flat
                            class="ma-4 pa-2 mt-2"
                        >                                                        
                            <v-card-text>
                                <v-row>
                                    <v-col
                                        cols="12"
                                    >
                                        <v-text-field
                                            solo
                                            readonly                                            
                                            :value="ideUrl"
                                        >
                                            <template #prepend-inner>
                                                <v-icon class="ml-1 mr-3">
                                                    mdi-microsoft-visual-studio-code
                                                </v-icon>
                                            </template>
                                            <template
                                                v-if="ideStatus === 'running'"
                                                v-slot:append
                                            >
                                                <v-btn
                                                    icon
                                                    :href="ideUrl"
                                                    target="_blank"
                                                >
                                                <v-icon dark>
                                                    mdi-open-in-new
                                                </v-icon>
                                                </v-btn>
                                            </template>
                                            <template v-slot:append-outer>
                                                <v-btn
                                                    fab
                                                    dark
                                                    small
                                                    class="mt-n2"
                                                    :loading="ideDeploying"
                                                    :color="ideButtonColor"
                                                    @click="ideButtonClick"
                                                >
                                                <v-icon dark>
                                                    {{ ideButtonIcon }}
                                                </v-icon>
                                                </v-btn>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col
                                        cols="12"
                                    >
                                        <v-text-field
                                            solo
                                            readonly
                                            :value="testUrl"
                                        >
                                            <template #prepend-inner>
                                                <h5 
                                                    class="mr-2" 
                                                    style="color: rgba(0,0,0,0.54)"
                                                >
                                                    TEST
                                                </h5>
                                            </template>                    
                                            <template
                                                v-if="ideStatus === 'running'"
                                                v-slot:append
                                            >
                                                <v-btn
                                                    icon
                                                    target="_blank"                                                    
                                                    :href="testUrl"
                                                >
                                                <v-icon dark>
                                                    mdi-open-in-new
                                                </v-icon>
                                                </v-btn>
                                            </template>      
                                            <template v-slot:append-outer>
                                                <v-btn
                                                    fab
                                                    dark
                                                    small
                                                    class="mt-n2"
                                                    style="visibility:hidden"
                                                />
                                            </template>                                                                                  
                                        </v-text-field>
                                    </v-col>
                                    <v-col
                                        cols="12"
                                    >
                                        <v-text-field
                                            solo
                                            readonly
                                            :value="notebookUrl"
                                        >
                                            <template #prepend-inner>
                                                <v-icon class="ml-1 mr-3">
                                                    $jupyter
                                                </v-icon>
                                            </template>                    
                                            <template
                                                v-if="notebookStatus === 'running'"
                                                v-slot:append
                                            >
                                                <v-btn
                                                    icon
                                                    target="_blank"                                                    
                                                    :href="notebookUrl"
                                                >
                                                <v-icon dark>
                                                    mdi-open-in-new
                                                </v-icon>
                                                </v-btn>
                                            </template>
                                            <template v-slot:append-outer>
                                                <v-btn
                                                    fab
                                                    dark
                                                    small
                                                    class="mt-n2"
                                                    :loading="notebookDeploying"
                                                    :color="notebookButtonColor"
                                                    @click="notebookButtonClick"
                                                >
                                                <v-icon dark>
                                                    {{ notebookButtonIcon }}
                                                </v-icon>
                                                </v-btn>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                </v-row>                                
                                <v-container>                                    
                                    <v-row class="mt-n2 mb-6">
                                        <v-spacer />
                                        <v-btn 
                                            color="deep-purple"
                                            class="white--text"
                                            depressed
                                            @click="save"
                                        >
                                            <v-icon left>
                                                mdi-content-save
                                            </v-icon> Save settings
                                        </v-btn>                                
                                    </v-row>
                                    <v-form>
                                        <v-jsf
                                            v-if="model !== null" 
                                            v-model="model" 
                                            :schema="schema" 
                                            :options="options"
                                        />
                                    </v-form>
                                </v-container>
                            </v-card-text>
                        </v-card>
                    </v-container>
                </v-sheet>
                <template #sidebar>
                    <infinity-sidebar
                        icon="$vuetify.icons.help"
                        title="docs/multiverse"
                    >
                        <h4 class="text-h4 mb-3 text--primary">
                        Settings for the platform
                        </h4>

                        <p class="text--secondary">
                            Settings for the platform can be set on this page
                        </p>

                        <v-btn
                            class="mr-0 white--text"
                            color="deep-purple"                            
                            min-width="100"
                            rounded
                        >
                            I want to know more
                        </v-btn>
                    </infinity-sidebar>
                </template>
            </infinity-page>
        </is-dev>
    </page-layout>
</template>
<script>
    import VJsf from '@koumoul/vjsf/lib/VJsf.js'
    import axios from 'axios'
    import PageLayout from '@/layouts/default/PageLayout'    
    import DeveloperDrawer from '@/layouts/Developer/Drawer'    
    import { mapState, mapActions } from 'pinia'
    import { useDevStore } from '@/store/dev-store'
    import schema from '@/assets/schemas/developer/settings.json'
    export default {
        components: {
            VJsf,
            PageLayout,
            DeveloperDrawer,
        },
        data: () => {
            return {
                ideInterval: null,
                notebookInterval: null,
                model: null,
                schema: schema,                
                options: {
                    httpLib: axios,
                    fieldProps: {
                        outlined: true,
                    },
                    arrayItemColProps: { cols: 12 },
                    arrayItemCardProps: {
                        outlined: true,
                        hover: true,
                        shaped: true,
                        style: 'margin: 10px',
                    },
                },
            }
        },
        computed: {
            ...mapState(useDevStore, ['settings', 'loading']),
            userInfo () {
                return this.parseJwt(this.$cookies.get('token'))
            },
            rfcUsername () {
                const username = this.userInfo?.preferred_username || ''
                return username.replaceAll(/[^a-zA-Z\d]/g, '')
            },
            ideUrl () {
                return `https://ide-${this.rfcUsername}.${document.location.host}`
            },
            testUrl () {
                return `https://test-${this.rfcUsername}.${document.location.host}`
            },
            notebookUrl () {
                return `https://notebook-${this.rfcUsername}.${document.location.host}`
            },
            ideStatus: {
                get () {
                    return this.$store.state.app.ideStatus
                },
                set (v) {
                    this.$store.commit('app/ideStatus', v)
                },
            },
            notebookStatus: {
                get () {
                    return this.$store.state.app.notebookStatus
                },
                set (v) {
                    this.$store.commit('app/notebookStatus', v)
                },
            },
            ideButtonColor () {
                switch (this.ideStatus) {
                    case 'stopped': return 'green'
                    case 'running': return 'red'
                    case 'deploying': return 'orange'
                }
                return 'red'
            },
            notebookButtonColor () {
                switch (this.notebookStatus) {
                    case 'stopped': return 'green'
                    case 'running': return 'red'
                    case 'deploying': return 'orange'
                }
                return 'red'
            },
            ideButtonIcon () {
                switch (this.ideStatus) {
                    case 'stopped': return 'mdi-play'
                    case 'running': return 'mdi-stop'
                }
                return ''
            },
            notebookButtonIcon () {
                switch (this.notebookStatus) {
                    case 'stopped': return 'mdi-play'
                    case 'running': return 'mdi-stop'
                }
                return ''
            },
            ideDeploying () {
                return this.ideStatus === 'deploying'
            },
            notebookDeploying () {
                return this.notebookStatus === 'deploying'
            },
        },
        watch: {
            settings (v) {                
                this.model = v
            },
        },
        async mounted () {
            await this.loadSettings()
            this.resetIdeInterval()
            this.resetNotebookInterval()
        },        
        unmounted () {
            clearInterval(this.ideInterval)
            clearInterval(this.notebookInterval)
        },
        beforeRouteLeave (to, from, next) {
            clearInterval(this.ideInterval)
            clearInterval(this.notebookInterval)
            next()
        },
        beforeDestroy () {
            clearInterval(this.ideInterval)
            clearInterval(this.notebookInterval)
        },
        deactivated () {
            clearInterval(this.ideInterval)
            clearInterval(this.notebookInterval)
        },
        methods: {
            ...mapActions(useDevStore, ['loadSettings', 'saveSettings']),
            parseJwt (token) {
                const base64Url = token.split('.')[1]
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
                const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(c => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                }).join(''))
                return JSON.parse(jsonPayload)
            },
            async save () {
                await this.saveSettings(this.model)
            },
            resetIdeInterval (skipLoad) {
                if (!skipLoad) {
                    this.$store.dispatch('app/getIdeStatus')
                }
                if (this.ideInterval) {
                    clearInterval(this.ideInterval)
                }
                this.ideInterval = setInterval(() => {
                    this.$store.dispatch('app/getIdeStatus')
                }, 5000)
            },
            resetNotebookInterval (skipLoad) {
                if (!skipLoad) {
                    this.$store.dispatch('app/getNotebookStatus')
                }
                if (this.ideInterval) {
                    clearInterval(this.ideInterval)
                }
                this.ideInterval = setInterval(() => {
                    this.$store.dispatch('app/getNotebookStatus')
                }, 5250)
            },
            async ideButtonClick () {
                switch (this.ideStatus) {
                case 'stopped':
                    this.ideStatus = 'deploying'
                    await this.$store.dispatch('app/startIde')
                    this.resetIdeInterval(true)
                    break
                case 'running':
                    this.ideStatus = 'deploying'
                    await this.$store.dispatch('app/stopIde')
                    this.resetIdeInterval(true)
                    break
                }
            },
            async notebookButtonClick () {
                switch (this.notebookStatus) {
                case 'stopped':
                    this.notebookStatus = 'deploying'
                    await this.$store.dispatch('app/startNotebook')
                    this.resetNotebookInterval(true)
                    break
                case 'running':
                    this.notebookStatus = 'deploying'
                    await this.$store.dispatch('app/stopNotebook')
                    this.resetNodeInterval(true)
                    break
                }
            },
        },
    }
</script>
<style scoped>
.vjsf >>> .v-card__title {
    font-size: 24px ! important;
    font-weight: 600;
    font-family: 'lato' ! important;
}
</style>
